import React, { RefObject, useRef, useCallback, useEffect } from "react";
import { Box, Typography, IconButton, Drawer, CircularProgress, useTheme as useMuiTheme } from "@mui/material";
import { Close as CloseIcon } from "@mui/icons-material";
import ThreadMessageList, { ThreadMessageMinimal } from "./ThreadMessageList";
import { useTheme } from "../contexts/ThemeContext";
import { APP_NAME } from "../helpers/constants";

/** Threshold in px from top to trigger load more when scrolling in the panel */
const LOAD_MORE_SCROLL_THRESHOLD = 100;
const LOAD_MORE_DEBOUNCE_MS = 150;

export interface AskPrism7PanelProps<T extends ThreadMessageMinimal> {
  /** Whether the drawer is open */
  open: boolean;
  /** Called when the drawer should close */
  onClose: () => void;
  /** Messages to show (typically filtered to user + system only) */
  messages: T[];
  /** Render a single message (bubble, avatar, content) */
  renderMessage: (message: T) => React.ReactNode;
  /** Optional: optimistic user message text while the post is in flight */
  temporaryUserMessage?: string | null;
  /** Optional: show AI "Thinking" placeholder */
  isAiThinking?: boolean;
  /** Optional: show AI "Generating draft" placeholder */
  isGeneratingDraft?: boolean;
  /** Current user for optimistic message avatar/name */
  user?: { name?: string; full_name?: string; email?: string } | null;
  /** Ref for scroll anchor at end of list */
  messagesEndRef?: RefObject<HTMLDivElement | null>;
  /** Message input UI (editor + attachments + buttons) fixed at bottom */
  messageInput: React.ReactNode;
  /** Optional: called when user scrolls near top to load older messages (pagination) */
  onLoadMore?: () => void;
  /** Whether more messages are currently being loaded */
  loadingMore?: boolean;
  /** Whether there are more older messages to load */
  hasMore?: boolean;
}

/** Right-side drawer for Ask Prism7: message list (user + AI) and fixed message input at bottom. */
function AskPrism7Panel<T extends ThreadMessageMinimal>({
  open,
  onClose,
  messages,
  renderMessage,
  temporaryUserMessage,
  isAiThinking,
  isGeneratingDraft,
  user,
  messagesEndRef,
  messageInput,
  onLoadMore,
  loadingMore = false,
  hasMore = true,
}: AskPrism7PanelProps<T>) {
  const muiTheme = useMuiTheme();
  const { isDarkMode } = useTheme();
  const panelScrollRef = useRef<HTMLDivElement>(null);
  const scrollTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const prevMessagesLengthRef = useRef(messages.length);
  const scrollPreserveRef = useRef<{ scrollHeight: number; scrollTop: number } | null>(null);

  // When user scrolls near top in the panel, trigger load more (same behavior as main content)
  const handlePanelScroll = useCallback(() => {
    const container = panelScrollRef.current;
    if (!container || !onLoadMore || loadingMore || !hasMore) return;
    if (container.scrollTop < LOAD_MORE_SCROLL_THRESHOLD) {
      if (scrollTimeoutRef.current) clearTimeout(scrollTimeoutRef.current);
      scrollTimeoutRef.current = setTimeout(() => {
        if (!loadingMore && hasMore) {
          scrollPreserveRef.current = {
            scrollHeight: container.scrollHeight,
            scrollTop: container.scrollTop,
          };
          onLoadMore();
        }
        scrollTimeoutRef.current = null;
      }, LOAD_MORE_DEBOUNCE_MS);
    }
  }, [onLoadMore, loadingMore, hasMore]);

  useEffect(() => {
    const container = panelScrollRef.current;
    if (!container || !open) return;
    container.addEventListener("scroll", handlePanelScroll);
    return () => {
      container.removeEventListener("scroll", handlePanelScroll);
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
        scrollTimeoutRef.current = null;
      }
    };
  }, [open, handlePanelScroll]);

  // Preserve panel scroll position after older messages are prepended (when load was triggered from panel)
  useEffect(() => {
    if (!onLoadMore || messages.length <= prevMessagesLengthRef.current) {
      prevMessagesLengthRef.current = messages.length;
      return;
    }
    const prevLen = prevMessagesLengthRef.current;
    prevMessagesLengthRef.current = messages.length;
    const preserved = scrollPreserveRef.current;
    scrollPreserveRef.current = null;
    const container = panelScrollRef.current;
    if (!container || !preserved) return;
    const newScrollHeight = container.scrollHeight;
    const scrollDifference = newScrollHeight - preserved.scrollHeight;
    requestAnimationFrame(() => {
      container.scrollTop = preserved.scrollTop + scrollDifference;
    });
  }, [messages.length, onLoadMore]);

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      sx={{
        "& .MuiDrawer-paper": {
          width: { xs: "100%", sm: 400 },
          maxWidth: "100%",
          boxSizing: "border-box",
          backgroundColor: muiTheme.palette.background.paper,
          borderLeft: `1px solid ${muiTheme.palette.divider}`,
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          height: "100%",
          minHeight: 0,
          minWidth: 0,
          overflow: "hidden",
        }}
      >
        {/* Header */}
        <Box
          sx={{
            p: 2,
            borderBottom: `1px solid ${muiTheme.palette.divider}`,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexShrink: 0,
            backgroundColor: muiTheme.palette.background.paper,
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Ask {APP_NAME || "Prism 7"}
          </Typography>
          <IconButton
            size="small"
            onClick={onClose}
            sx={{ color: "text.secondary" }}
            aria-label={`Close Ask ${APP_NAME || "Prism 7"} panel`}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Scrollable message list - vertical scroll only; long URLs/text wrap */}
        <Box
          ref={panelScrollRef}
          sx={{
            flex: 1,
            minHeight: 0,
            minWidth: 0,
            overflowX: "hidden",
            overflowY: "auto",
            p: 1,
            wordWrap: "break-word",
            wordBreak: "break-word",
            overflowWrap: "break-word",
          }}
        >
          {/* Loading more indicator at top when paginating from panel scroll */}
          {onLoadMore && loadingMore && (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                py: 1.5,
                gap: 1,
              }}
            >
              <CircularProgress size={20} />
              <Typography variant="caption" color="text.secondary">
                Loading older messages...
              </Typography>
            </Box>
          )}
          <ThreadMessageList
            messages={messages}
            renderMessage={renderMessage}
            temporaryUserMessage={temporaryUserMessage}
            isAiThinking={isAiThinking}
            isGeneratingDraft={isGeneratingDraft}
            user={user}
            messagesEndRef={messagesEndRef}
          />
        </Box>

        {/* Fixed message input at bottom - no horizontal overflow */}
        <Box
          sx={{
            flexShrink: 0,
            minWidth: 0,
            maxWidth: "100%",
            overflow: "hidden",
            p: 1,
            borderTop: `1px solid ${muiTheme.palette.divider}`,
            backgroundColor: muiTheme.palette.background.paper,
          }}
        >
          {messageInput}
        </Box>
      </Box>
    </Drawer>
  );
}

export default AskPrism7Panel;
